/**
 * UnifiedTimeFilter — single dropdown for all time-based filtering.
 *
 * Three sections:
 * 1. Quick presets (All time, This week, This month, This quarter, YTD, Last quarter)
 * 2. Pay periods (active + recent from period queue)
 * 3. Custom date range (two date inputs)
 *
 * Reads/writes the global TimeFilterContext so the selection persists across
 * screen navigation.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { shortDate } from '../format';
import {
  PRESET_LABELS,
  useTimeFilter,
  type Preset,
  type TimeSelection,
} from '../context/TimeFilterContext';
import type { PeriodQueueItem } from '../api';

const PRESETS: Preset[] = ['all', 'week', 'month', 'quarter', 'ytd', 'lastQuarter'];

function progressColor(item: PeriodQueueItem): string {
  if (item.status === 'closed') return 'bg-profit';
  if (item.status === 'locked') return 'bg-blue-500';
  if (item.isOverdue) return 'bg-caution';
  if (item.stagesComplete > 0) return 'bg-sky-400';
  return 'bg-ink-faint/30';
}

function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    open: 'Open',
    in_progress: 'In Progress',
    locked: 'Locked',
    closed: 'Closed',
  };
  return labels[status] ?? status;
}

function isPresetActive(selection: TimeSelection, preset: Preset): boolean {
  if (preset === 'all') return selection.mode === 'all';
  return selection.mode === 'range' && selection.preset === preset;
}

function isPeriodActive(selection: TimeSelection, periodId: string): boolean {
  return selection.mode === 'period' && selection.periodId === periodId;
}

export function UnifiedTimeFilter() {
  const tf = useTimeFilter();
  const [open, setOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handlePreset = useCallback(
    (preset: Preset) => {
      tf.selectPreset(preset);
      setShowCustom(false);
      setOpen(false);
    },
    [tf],
  );

  const handlePeriod = useCallback(
    (periodId: string) => {
      tf.selectPeriod(periodId);
      setShowCustom(false);
      setOpen(false);
    },
    [tf],
  );

  const handleCustomApply = useCallback(() => {
    if (customFrom && customTo) {
      tf.selectCustomRange(customFrom, customTo);
      setShowCustom(false);
      setOpen(false);
    }
  }, [customFrom, customTo, tf]);

  const isNonDefault =
    tf.selection.mode !== 'all' &&
    !(tf.selection.mode === 'range' && tf.selection.preset === 'ytd');

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
          isNonDefault
            ? 'border-profit/40 bg-profit/5 text-profit hover:border-profit/60'
            : 'border-edge bg-surface-base text-ink-muted hover:border-edge-strong hover:text-ink'
        }`}
      >
        <svg
          className="h-3.5 w-3.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        {tf.displayLabel}
        <svg
          className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-[420px] max-h-[70vh] overflow-auto rounded-lg border border-edge bg-surface-raised shadow-lg">
          {/* ── Presets ── */}
          <div className="p-1">
            <div className="px-3 pt-2 pb-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-faint">
                Date Range
              </span>
            </div>
            {PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => handlePreset(p)}
                className={`w-full rounded-md px-3 py-1.5 text-left text-xs transition-colors ${
                  isPresetActive(tf.selection, p)
                    ? 'bg-profit/10 font-medium text-profit'
                    : 'text-ink-muted hover:bg-surface-overlay/50 hover:text-ink'
                }`}
              >
                {PRESET_LABELS[p]}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setShowCustom(!showCustom)}
              className={`w-full rounded-md px-3 py-1.5 text-left text-xs transition-colors ${
                tf.selection.mode === 'range' && tf.selection.preset === 'custom'
                  ? 'bg-profit/10 font-medium text-profit'
                  : 'text-ink-muted hover:bg-surface-overlay/50 hover:text-ink'
              }`}
            >
              Custom range
            </button>
            {showCustom && (
              <div className="mx-1 mt-1 rounded-md border border-edge bg-surface-overlay/30 p-3 space-y-2">
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    className="w-full rounded border border-edge bg-surface-base px-2 py-1 text-xs text-ink"
                  />
                  <input
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    className="w-full rounded border border-edge bg-surface-base px-2 py-1 text-xs text-ink"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleCustomApply}
                  disabled={!customFrom || !customTo}
                  className="w-full rounded-md bg-profit px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-profit/90 disabled:opacity-50"
                >
                  Apply
                </button>
              </div>
            )}
          </div>

          {/* ── Pay Periods ── */}
          {tf.periodQueue && (
            <div className="border-t border-edge">
              {tf.periodQueue.activePeriods.length > 0 && (
                <PeriodGroup
                  label="Active Pay Periods"
                  items={tf.periodQueue.activePeriods}
                  selection={tf.selection}
                  recommendedId={tf.periodQueue.recommendedPeriodId}
                  onSelect={handlePeriod}
                />
              )}
              {tf.periodQueue.recentHistory.length > 0 && (
                <PeriodGroup
                  label="Recent History"
                  items={tf.periodQueue.recentHistory}
                  selection={tf.selection}
                  recommendedId={tf.periodQueue.recommendedPeriodId}
                  onSelect={handlePeriod}
                />
              )}
            </div>
          )}

          {tf.periodQueueLoading && (
            <div className="border-t border-edge px-4 py-3 text-center text-xs text-ink-faint">
              Loading pay periods…
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PeriodGroup({
  label,
  items,
  selection,
  recommendedId,
  onSelect,
}: {
  label: string;
  items: readonly PeriodQueueItem[];
  selection: TimeSelection;
  recommendedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div>
      <div className="px-4 pt-3 pb-1">
        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-faint">
          {label}
        </span>
      </div>
      <div className="divide-y divide-edge/50">
        {items.map((item) => {
          const isActive = isPeriodActive(selection, item.id);
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelect(item.id)}
              className={`w-full px-4 py-2 text-left transition-colors hover:bg-surface-overlay/60 ${
                isActive ? 'bg-profit/5' : ''
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  {isActive && (
                    <svg
                      className="h-3.5 w-3.5 shrink-0 text-profit"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  <div className="min-w-0">
                    <span
                      className={`text-xs font-medium ${isActive ? 'text-profit' : 'text-ink-muted'}`}
                    >
                      Week ending {shortDate(item.weekEnding)}
                    </span>
                    {item.id === recommendedId && !isActive && (
                      <span className="ml-2 inline-flex items-center rounded bg-sky-50 border border-sky-200 px-1.5 py-px text-[10px] font-medium text-sky-700">
                        Next up
                      </span>
                    )}
                    {item.isOverdue && (
                      <span className="ml-2 text-[10px] text-amber-600">
                        {item.daysOverdue}d overdue
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <div className="flex gap-0.5">
                    {Array.from({ length: item.stagesTotal }, (_, i) => (
                      <div
                        key={i}
                        className={`h-1 w-2.5 rounded-sm ${
                          i < item.stagesComplete ? progressColor(item) : 'bg-ink-faint/15'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-[10px] text-ink-faint">{statusLabel(item.status)}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
